import Pubnub from 'pubnub';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { EMOJI_TYPES } from 'client/types';
import { RootState } from '../../..';
import { getErrorText } from '../helpers';
import _ from 'lodash';

type FetchMessagesParams = {
  channels: string[];
  count: number;
  start?: string;
};

export const getReactionsMessages = createAsyncThunk<
  { channels: Pubnub.FetchMessagesResponse['channels'] },
  { roomId: string | Array<string>; isLoadMore?: boolean },
  { rejectValue: string }
>('pubnub/getReactionsMessages', async (data, { rejectWithValue, getState }) => {
  try {
    const pubnubReducer = (getState() as RootState).pubnub;

    if (!pubnubReducer.pubnub) {
      throw new Error('PubnubInstanceIsNull');
    }

    const { messages } = pubnubReducer?.reactions;
    const fetchMessagesParams: FetchMessagesParams = {
      channels: typeof data.roomId === 'string' ? [data.roomId] : data.roomId,
      count: 15,
    };

    // Use if click on load more btn in single reaction tab
    if (data.isLoadMore && typeof data.roomId === 'string') {
      const targetKey = data.roomId.split('.').pop();
      const lastMessage = targetKey ? messages[targetKey as EMOJI_TYPES].slice(-1)[0] : undefined;

      if (lastMessage) {
        fetchMessagesParams.start = `${lastMessage.timetoken}`;
      }
    }

    if (data.isLoadMore && typeof data.roomId === 'string') {
      const result = await pubnubReducer.pubnub.fetchMessages(fetchMessagesParams);

      return { channels: result.channels };
    }

    if (data.isLoadMore && _.isArray(data.roomId)) {
      const promises: Array<Promise<Pubnub.FetchMessagesResponse>> = [];
      const channels: Pubnub.FetchMessagesResponse['channels'] = {};

      _.forEach(data.roomId, (channelString) => {
        const targetKey = channelString.split('.').pop();
        const lastMessage = targetKey ? messages[targetKey as EMOJI_TYPES]?.slice(-1)[0] : undefined;
        const fetchParams: FetchMessagesParams = {
          channels: [channelString],
          count: 15,
        };

        if (lastMessage) {
          fetchParams.start = `${lastMessage.timetoken}`;
        }

        if (pubnubReducer.pubnub) {
          promises.push(pubnubReducer.pubnub.fetchMessages(fetchParams));
        }
      });

      const responses = await Promise.all(promises);

      _.forEach(responses, (response) => {
        const entries = Object.entries(response.channels);
        const channelName = !_.isEmpty(entries) ? entries[0][0] : '';
        const messages = !_.isEmpty(entries) ? entries[0][1] : undefined;

        if (channelName && messages) {
          channels[channelName] = messages;
        }
      });

      return { channels: channels };
    }

    const result = await pubnubReducer.pubnub.fetchMessages(fetchMessagesParams);
    return { channels: result.channels };
  } catch (e) {
    return rejectWithValue(getErrorText(e as Error));
  }
});
